/*
    The campaign's per-network split, on its way to the server (MPN-1792).

    A window's `pinned` record holds exactly the networks an operator set by hand on that
    window -- the rows the table marks "Edited". Those become {PACE, NETWORK} policies; every
    other network has no row and falls through to the campaign's own {NETWORK} policy, which
    is what the resolver already does and what "Edited" means on the screen.

    So the list sent for a window is the pinned entries, plus any network or ad group paused
    on it (MPN-1893) -- a pause has nowhere else to live, because it is part of the same
    policy spec as the percent. An empty list is meaningful rather than wasteful: it says the
    window overrides nothing, and the server removes any override it used to have. That is
    Reset.

    Daily Cap is the exception to all of this. It is a ceiling on the roll-forward a network
    applies within a day, not a share of this window's dollars, so it belongs to the network
    across the whole campaign (MPN-1806) and is written as a plain {NETWORK} policy.
*/
import { Segment } from "./segmentEngine"
import { adGroupOnIn, networkOnIn } from "./floors"
import type { SegmentNetwork } from "./SegmentNetworksTable"
import type {
  LaunchBudgetAdGroupPayload,
  LaunchBudgetSegmentNetworkPayload,
} from "@services/engagements/budget/commands/cmdSaveLaunchBudget"

/** A typed cap becomes a number; a cleared field becomes null, which removes the ceiling. */
export const parseCap = (value: string | undefined): number | null | undefined => {
  if (value === undefined) return undefined
  const trimmed = value.trim()
  // Blank is a real value meaning "no additional ceiling" -- the Budget screen's own field
  // says so ("leave blank for no cap") and 92 of the 590 network policies in production
  // are in that state. It does not mean unbounded: the network still receives its share of
  // the campaign's Amount divided by the funded days. The cap only ever lowers that.
  if (trimmed === "" || trimmed === "—") return null
  const parsed = Number(trimmed.replace(/,/g, ""))
  if (!Number.isFinite(parsed) || parsed < 0) return undefined
  return Math.round(parsed * 100) / 100
}

/** The cap in force for a network: the operator's unsaved edit, else what the tree holds. */
export const capFor = (
  network: SegmentNetwork,
  capEdits: Record<string, string>,
): number | null => {
  const edited = parseCap(capEdits[network.key])
  return edited === undefined ? (network.dailyCapAmount ?? null) : edited
}

/**
 * One window's overrides.
 *
 * The cap and the bid are copied from the network rather than left unset. A window-scoped
 * policy beats the campaign's outright and the server rebuilds the whole spec, so a
 * percent-only override would silently drop the campaign's Daily Cap for that window --
 * and 494 of the 586 network policies in production carry one. The cap copied in is the
 * EDITED one where there is an edit, otherwise a window override would re-assert the old
 * ceiling the moment the operator raised it.
 */
export const windowNetworks = (
  segment: Segment,
  networks: readonly SegmentNetwork[],
  capEdits: Record<string, string> = {},
): LaunchBudgetSegmentNetworkPayload[] => {
  // Union rather than `pinned` alone. A pause always writes a 0% pin beside it today, so the
  // two sets match -- but the pause has nowhere else to live, and a network whose pin went
  // missing would silently come back unpaused on the next load.
  const keys = new Set<string>([
    ...Object.keys(segment.pinned || {}),
    ...Object.keys(segment.netOn || {}).filter(
      (key) => !(segment.netOn as Record<string, boolean>)[key],
    ),
  ])

  return [...keys].flatMap((key) => {
    const network = networks.find((n) => n.key === key)
    if (!network) return []
    return [
      {
        network: key.toUpperCase(),
        percent: (segment.pinned || {})[key] ?? 0,
        daily_spend_cap: capFor(network, capEdits),
        bid: network.bidAmount ?? null,
        // Paused for this window (MPN-1893). Sent as its own flag rather than inferred from
        // the 0%: a resume deliberately leaves the network on at 0% waiting for a share, and
        // the percent alone cannot tell the two apart.
        is_paused: !networkOnIn(segment, network),
        ad_groups: pausedAdGroups(segment, network),
      },
    ]
  })
}

/**
 * The ad groups turned OFF on this network in this window, and only those.
 *
 * An ad group that is running sends nothing, which is the rule this file already follows for
 * networks: one edit, one policy. Sending the inherited split back for every ad group would
 * freeze it against later edits on the Budget screen.
 *
 * The cost of the exception is explicit: pausing an ad group does pin its share for this
 * window, because the flag and the percent are one policy spec and the server rebuilds the
 * whole spec from what it is sent. Resuming removes the row entirely, so the ad group goes
 * back to inheriting -- which is also why a resume is not sent as `is_paused: false`.
 */
const pausedAdGroups = (
  segment: Segment,
  network: SegmentNetwork,
): LaunchBudgetAdGroupPayload[] =>
  network.adGroups.flatMap((adGroup) =>
    adGroupOnIn(segment, network, adGroup)
      ? []
      : [
          {
            ad_group_id: Number(adGroup.key),
            // The policy's own percent, not `share`. A paused network resolves every ad
            // group under it to $0, so a share-derived percent would write 0 and pin the
            // ad group at nothing for that window.
            percent: adGroup.percentOfNetwork ?? Math.round((adGroup.share || 0) * 10000) / 100,
            daily_spend_cap: null,
            bid: adGroup.bidAmount ?? null,
            is_paused: true,
          },
        ],
  )

/**
 * The campaign-wide rows, which this screen writes only for networks whose cap changed.
 *
 * Deliberately not every network. The server rebuilds a policy's whole spec from what it
 * is sent, so writing a row for an untouched network would turn its inherited equal split
 * into an explicit percent -- and then adding a network later would leave the campaign
 * over-allocated instead of rebalancing. One edit, one policy, which is exactly what the
 * Budget screen's own per-network save does.
 */
export const campaignNetworks = (
  networks: readonly SegmentNetwork[],
  capEdits: Record<string, string>,
  /** This network's current share. Undefined where it cannot be resolved, which skips the
   *  row entirely -- see below. */
  percentFor: (key: string) => number | undefined,
): LaunchBudgetSegmentNetworkPayload[] =>
  networks.flatMap((network) => {
    const edited = parseCap(capEdits[network.key])
    if (edited === undefined) return []
    // Clearing a field that was already blank changes nothing, so it earns no policy write
    // and no history entry. Same rule as the Budget screen's CapInput.
    if (edited === null && network.dailyCapAmount == null) return []
    if (edited !== null && edited === network.dailyCapAmount) return []
    // Carried, not changed: this screen owns the cap, the percent belongs to whoever set
    // it. A network whose share cannot be resolved is skipped rather than defaulted --
    // the server rebuilds the whole spec, so sending a fallback of 0 would set that
    // network's allocation to nothing as a side effect of editing its cap.
    const percent = percentFor(network.key)
    if (percent == null) return []
    return [
      {
        network: network.key.toUpperCase(),
        percent,
        daily_spend_cap: edited,
        bid: network.bidAmount ?? null,
      },
    ]
  })
